import { useMenuStore } from './menu'
import { API_URL } from '@/constants/api'
import apiHelper from '@/utils/apiHelper'
import type { AccountState, AuthState, User } from '@/types/user'
import type { Menu } from '@/types/menu'
import { camelCase } from 'change-case/keys'
import { jwtDecode, type JwtPayload } from 'jwt-decode'
import { USER_ROLE } from '@/constants/consts'

// 썸네일·대표 이미지 요청(<img> 태그, 뷰어 fetch)은 Authorization 헤더를 붙일 수 없어 서버가 이 두 API에서만
// 쿠키의 액세스 토큰을 인정한다(04 사용자인증·보안 5.3). 액세스 토큰이 바뀔 때마다 쿠키도 같이 맞춘다.
const IMAGE_TOKEN_COOKIE = 'access_token'
const IMAGE_TOKEN_COOKIE_PATH = '/api/data'

function syncImageTokenCookie(token: string | null | undefined) {
  // Secure 쿠키는 HTTPS에서만 저장되므로, HTTP로 띄운 개발 서버에서는 Secure 없이 설정한다.
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = token
    ? `${IMAGE_TOKEN_COOKIE}=${token}; Path=${IMAGE_TOKEN_COOKIE_PATH}; SameSite=Strict${secure}`
    : `${IMAGE_TOKEN_COOKIE}=; Path=${IMAGE_TOKEN_COOKIE_PATH}; Max-Age=0; SameSite=Strict${secure}`
}

export const useAccountStore = defineStore('account', {
  state: (): AccountState => {
    const token = localStorage.getItem('access_token')
    const refresh = localStorage.getItem('refresh_token')
    let initialUserNo: number | null = null
    let initialUserId: string | null = null
    let initialUserName: string | null = null
    let initialRoleId: string | null = null
    let initialMenus: string[] | null = null

    const storedUrl = localStorage.getItem('seaweedfs_url') || ''

    // 새로고침 등으로 브라우저 쿠키가 비어 있어도 저장된 토큰으로 다시 맞춘다.
    syncImageTokenCookie(token)

    if (token) {
      try {
        const decoded = jwtDecode<JwtPayload>(token)
        const userData = camelCase(decoded) as any
        initialUserNo = userData.userNo ?? null
        initialUserId = userData.userId ?? null
        initialUserName = userData.userName ?? null
        initialRoleId = userData.roleId ?? null
        if (userData.menu) {
          initialMenus = Array.isArray(userData.menu) ? userData.menu : []
        }
      } catch (e) {
        console.error('Failed to decode stored JWT token:', e)
      }
    }

    return {
      accountVal: 'pinia',
      accessToken: token,
      refreshToken: refresh,
      userNo: initialUserNo,
      userId: initialUserId,
      userName: initialUserName,
      seaweedfsUrl: storedUrl,
      defaultMenu: ['HOME', 'ERROR', 'LOGIN', 'LOGOUT'],
      menu: null,
      menus: initialMenus,
      roleId: initialRoleId,
      phoneNo: null,
      interval: null,
    }
  },
  getters: {
    accountState(state) {
      return state
    },
    shortName(): string {
      if (this.userName) {
        return this.userName.substring(0, 2).toUpperCase()
      }
      return ''
    },
    roleName() {
      switch (this.roleId) {
        case USER_ROLE.ADMIN:
          return '관리자'
        case USER_ROLE.PROJECT_MANAGER:
          return '프로젝트 관리자'
        case USER_ROLE.RESEARCHER:
          return '연구원'
        case USER_ROLE.COOPERATION_PARTNER:
          return '협력 파트너'
        default:
          return '사용자'
      }
    },
  },
  actions: {
    setSession(payload: AuthState) {
      this.$state.accessToken = payload.accessToken
      this.$state.refreshToken = payload.refreshToken
      if (payload.accessToken) {
        localStorage.setItem('access_token', payload.accessToken)
        syncImageTokenCookie(payload.accessToken)
      }
      if (payload.refreshToken) {
        localStorage.setItem('refresh_token', payload.refreshToken)
      }
      if (payload.seaweedfsUrl) {
        this.$state.seaweedfsUrl = payload.seaweedfsUrl
        localStorage.setItem('seaweedfs_url', payload.seaweedfsUrl)
      }
      try {
        const decoded = jwtDecode<JwtPayload>(payload.accessToken)
        const userData = camelCase(decoded) as AccountState
        this.setUser(userData)
      } catch (e) {
        console.error('Failed to decode JWT token:', e)
      }
    },
    setUser(payload: AccountState) {
      this.$state.userNo = payload.userNo
      this.$state.userId = payload.userId
      this.$state.userName = payload.userName
      this.$state.menu = payload.menu
      this.$state.roleId = payload.roleId

      // Flatten menu tree to get a list of all accessible menu IDs
      const flattenMenu = (items: any[]): string[] => {
        let result: string[] = []
        items.forEach((item) => {
          if (typeof item === 'string') {
            result.push(item)
          } else if (item && typeof item === 'object') {
            if (item.menuId) result.push(item.menuId)
            if (item.children && item.children.length > 0) {
              result = result.concat(flattenMenu(item.children))
            }
          }
        })
        return result
      }

      if (payload.menu) {
        const menuArray = Array.isArray(payload.menu) ? payload.menu : [payload.menu]
        this.$state.menus = flattenMenu(menuArray)
      } else if (!this.$state.menus) {
        this.$state.menus = []
      }
    },
    async fetchMenus() {
      try {
        const res = await apiHelper.get<Menu[]>(API_URL.MENU.LIST)
        if (res.errorCode === 0 && res.data) {
          const menuStore = useMenuStore()
          const menusData = res.data
          menuStore.setMenus(menusData)
          this.setUser({ ...this.$state, menu: menusData as any })
          return menusData
        }
      } catch (e) {
        console.error('Failed to fetch menus from API:', e)
      }
      return null
    },
    setUserInfo(payload: User) {
      this.$state.userName = payload.userName
    },
    changeUserInfo(payload: User) {
      this.$state.userName = payload.userName
      this.$state.phoneNo = payload.phoneNo
    },

    delSession() {
      const menuStore = useMenuStore()
      menuStore.clearMenus()

      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('seaweedfs_url')
      syncImageTokenCookie(null)

      this.$state.userNo = null
      this.$state.userId = null
      this.$state.userName = null
      this.$state.seaweedfsUrl = null
      this.$state.defaultMenu = ['HOME', 'ERROR', 'LOGIN', 'LOGOUT']
      this.$state.menu = null
      this.$state.menus = null
      this.$state.roleId = null
      this.$state.phoneNo = null
      this.$state.accessToken = null
      this.$state.refreshToken = null
      this.$state.interval = null
    },

    clearInterval() {
      if (this.$state.interval) {
        window.clearInterval(this.$state.interval)
      }
      this.$state.interval = null
    },

    // Actions
    async refresh() {
      const res = await apiHelper.post<{ accessToken: string }>(API_URL.AUTH.REFRESH, {})
      if (res.errorCode === 0 && res.data) {
        this.$state.accessToken = res.data.accessToken
        if (res.data.accessToken) {
          localStorage.setItem('access_token', res.data.accessToken)
          syncImageTokenCookie(res.data.accessToken)
        }
        return true
      }
      return false
    },

    refreshAll(payload: AuthState) {
      this.$state.accessToken = payload.accessToken
      this.$state.refreshToken = payload.refreshToken
      if (payload.accessToken) {
        localStorage.setItem('access_token', payload.accessToken)
        syncImageTokenCookie(payload.accessToken)
      }
      if (payload.refreshToken) localStorage.setItem('refresh_token', payload.refreshToken)
    },

    $reset() {
      this.delSession()
    },

    intervalClear() {
      this.clearInterval()
    },
  },
})
